import * as React from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import { savedViewsRequest, insightsError } from 'Src/helpers/service/sltInsights';
import { useCloseOnOutside } from 'Src/hooks/useCloseOnOutside';
import { Card } from './OverviewContent';
import './SavedViews.scss';
const titles = { overview: 'Overview', clubs: 'Clubs', sport: 'Sport', students: 'Students', reports: 'Reports' };
const segments = {
  notyet: 'Not yet taken part',
  stopped: 'Stopped taking part',
  never: 'No recorded participation',
  retained: 'Retained participants',
  ppnot: 'PP not reached',
  occasional: 'Occasional only, KS4',
  all: 'All students'
};
const keys = [
  'from',
  'to',
  'population',
  'archiveAt',
  'area',
  'years',
  'characteristic',
  'characteristic2',
  'segment',
  'sort',
  'direction',
  'measure',
  'clubId',
  'sportId',
  'eventId',
  'tournamentId',
  'tableYear',
  'tableGroup'
];
const clean = value =>
  JSON.stringify({
    ...value,
    query: Object.keys(value.query || {})
      .sort()
      .reduce((all, key) => ({ ...all, [key]: value.query[key] }), {})
  });
export function useSavedViews({
  user,
  tab,
  params,
  density,
  onDensity
}: {
  user: any;
  tab: string;
  params: URLSearchParams;
  density: string;
  onDensity: (value: string) => void;
}) {
  const history = useHistory(),
    location = useLocation(),
    [views, setViews] = React.useState<any>({ mine: [], shared: [] }),
    [open, setOpen] = React.useState(false),
    [saving, setSaving] = React.useState(false),
    [name, setName] = React.useState(''),
    [shared, setShared] = React.useState(false),
    [busy, setBusy] = React.useState(false),
    [error, setError] = React.useState(''),
    [toast, setToast] = React.useState(false),
    [menu, setMenu] = React.useState(''),
    [rename, setRename] = React.useState(''),
    [renameValue, setRenameValue] = React.useState('');
  const menuRef = React.useRef<HTMLDivElement>(null),
    saveRef = React.useRef<HTMLDivElement>(null),
    bookmark = React.useRef(!!params.get('view') && !params.get('from')),
    applied = React.useRef('');
  const state = {
    tab: titles[tab] ? tab : 'overview',
    query: keys.reduce((q, key) => (params.get(key) ? { ...q, [key]: params.get(key) } : q), {}),
    ...(params.get('period') ? { period: params.get('period') } : {}),
    compare: params.get('compare') || 'none',
    ...(tab === 'overview' ? { density } : {})
  };
  (state.query as any).area = tab === 'clubs' ? 'clubs' : tab === 'sport' ? 'sport' : params.get('area') || 'all';
  if (tab === 'students') {
    (state.query as any).measure = 'participation';
    (state.query as any).segment = params.get('segment') || 'notyet';
  }
  const all = [...views.mine, ...views.shared],
    active = all.find(view => view.id === params.get('view')),
    modified = active && clean(active.state) !== clean(state);
  const summary = (value: any) =>
    [
      titles[value.tab],
      segments[value.query.segment],
      [value.query.characteristic, value.query.characteristic2, value.query.tableGroup]
        .filter(Boolean)
        .map(
          filter =>
            ({
              'pupilsPremium:yes': 'PP',
              'freeSchoolMeals:yes': 'FSM',
              'specialEducationNeeds:yes': 'SEND',
              'gender:FEMALE': 'Girls',
              'gender:MALE': 'Boys'
            }[filter] || filter)
        )
        .join(' · '),
      value.query.years ? 'Y' + value.query.years.split(',').join(', Y') : null,
      value.query.tableYear ? 'Y' + value.query.tableYear : null,
      {
        'academic-current': 'This academic year',
        'academic-previous': 'Past academic year',
        'term-current': 'Current term',
        'month-current': 'This month',
        'week-current': 'This week'
      }[value.period] || [value.query.from, value.query.to].filter(Boolean).join('–')
    ]
      .filter(Boolean)
      .join(' · ');
  const refresh = () => savedViewsRequest(user).then(value => setViews(value));
  React.useEffect(() => {
    let current = true;
    setViews({ mine: [], shared: [] });
    savedViewsRequest(user)
      .then(value => {
        if (current) setViews(value);
      })
      .catch(e => {
        if (current) setError(insightsError(e));
      });
    return () => {
      current = false;
    };
  }, [user.activeSchoolId, user.roleSessionKey]);
  useCloseOnOutside({ isOpen: open, onClose: () => setOpen(false), wrapRef: menuRef });
  useCloseOnOutside({ isOpen: saving, onClose: () => setSaving(false), wrapRef: saveRef });
  React.useEffect(() => {
    const close = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        setOpen(false);
        setSaving(false);
        setMenu('');
      }
    };
    document.addEventListener('keydown', close);
    return () => document.removeEventListener('keydown', close);
  }, []);
  const apply = (view: any) => {
    const next = new URLSearchParams();
    next.set('view', view.id);
    Object.keys(view.state.query).forEach(key => next.set(key, view.state.query[key]));
    if (view.state.period) next.set('period', view.state.period);
    next.set('compare', view.state.compare || 'none');
    if (view.state.tab === 'overview') onDensity(view.state.density || 'calm');
    applied.current = view.id;
    bookmark.current = false;
    setOpen(false);
    setSaving(false);
    setError('');
    history.push({ pathname: '/slt/' + view.state.tab, search: '?' + next.toString() });
    savedViewsRequest(user, view.id, 'patch', { opened: true })
      .then(refresh)
      .catch(() => {});
  };
  React.useEffect(() => {
    if (bookmark.current && active && applied.current !== active.id) apply(active);
  }, [active?.id]);
  const begin = () => {
    setName(
      summary(state)
        .replace(/^Students · /, '')
        .slice(0, 100) || titles[tab]
    );
    setShared(false);
    setError('');
    setSaving(true);
    setOpen(false);
  };
  const save = async () => {
    setBusy(true);
    setError('');
    try {
      const value = await savedViewsRequest(user, '', 'post', { name, shared, state });
      await refresh();
      const next = new URLSearchParams(location.search);
      next.set('view', value.id);
      applied.current = value.id;
      history.replace({ pathname: location.pathname, search: '?' + next.toString() });
      setSaving(false);
      setToast(true);
    } catch (e) {
      setError(insightsError(e));
    } finally {
      setBusy(false);
    }
  };
  const update = async (view: any, body: any) => {
    setBusy(true);
    setError('');
    try {
      await savedViewsRequest(user, view.id, 'patch', body);
      await refresh();
      setMenu('');
      setRename('');
    } catch (e) {
      setError(insightsError(e));
    } finally {
      setBusy(false);
    }
  };
  const actions = (view: any) => (
    <div className="slv-actions">
      <button
        type="button"
        className="sli-link"
        aria-label={'Actions for ' + view.name}
        onClick={() => setMenu(menu === view.id ? '' : view.id)}
      >
        ⋯
      </button>
      {menu === view.id && (
        <div className="slv-actionMenu">
          {views.mine.some(v => v.id === view.id) ? (
            <>
              <button
                onClick={() => {
                  setRename(view.id);
                  setRenameValue(view.name);
                  setMenu('');
                }}
              >
                Rename
              </button>
              <button disabled={busy} onClick={() => update(view, { shared: !view.shared })}>
                {view.shared ? 'Unshare' : 'Share with SLT'}
              </button>
              <button
                disabled={busy}
                onClick={async () => {
                  try {
                    await savedViewsRequest(user, view.id, 'delete');
                    await refresh();
                    setMenu('');
                    if (active?.id === view.id) {
                      const next = new URLSearchParams(location.search);
                      next.delete('view');
                      history.replace({ pathname: location.pathname, search: next.toString() });
                    }
                  } catch (e) {
                    setError(insightsError(e));
                  }
                }}
              >
                Delete
              </button>
            </>
          ) : (
            <span>Only the owner can change this view.</span>
          )}
        </div>
      )}
    </div>
  );
  const row = (view: any, full = false) => (
    <div
      className="slv-view"
      key={view.id}
      onClick={event => {
        if (!(event.target as HTMLElement).closest('button,input,form')) apply(view);
      }}
    >
      {rename === view.id ? (
        <form
          onSubmit={e => {
            e.preventDefault();
            update(view, { name: renameValue });
          }}
        >
          <input
            aria-label="Rename view"
            maxLength={100}
            value={renameValue}
            onChange={e => setRenameValue(e.target.value)}
          />
          <button className="sli-link" disabled={busy}>
            Save
          </button>
        </form>
      ) : (
        <button className="slv-viewName" onClick={() => apply(view)}>
          <b>{view.name}</b>
          <small>{summary(view.state)}</small>
        </button>
      )}
      {full && (
        <span className="slv-owner">
          {view.owner}
          <small>
            {view.lastOpenedAt ? 'Opened ' + new Date(view.lastOpenedAt).toLocaleDateString('en-GB') : 'Not opened yet'}
          </small>
        </span>
      )}
      {!full && views.shared.some(v => v.id === view.id) && <small>{view.owner}</small>}
      {actions(view)}
    </div>
  );
  const header = (
    <div className="slv-saveAnchor" ref={saveRef}>
      {!['clubs', 'sport'].includes(tab) && (
        <button type="button" className="sli-button" onClick={begin}>
          {tab === 'students' ? 'Save as view' : 'Save view'}
        </button>
      )}
      {saving && (
        <div className="slv-popover" role="dialog" aria-label="Save view">
          <label>
            Name
            <input autoFocus maxLength={100} value={name} onChange={e => setName(e.target.value)} />
          </label>
          <p>{summary(state)}</p>
          <label className="slv-share">
            <input type="checkbox" checked={shared} onChange={e => setShared(e.target.checked)} />
            Share with SLT
          </label>
          {error && <p role="alert">{error}</p>}
          <div>
            <button type="button" className="sli-button sli-primary" disabled={busy || !name.trim()} onClick={save}>
              Save
            </button>
            <button type="button" className="sli-link" onClick={() => setSaving(false)}>
              Cancel
            </button>
          </div>
        </div>
      )}
    </div>
  );
  const toolbar = (
    <div className="slv-toolbar" ref={menuRef}>
      <button
        type="button"
        className="sli-filter"
        aria-haspopup="menu"
        aria-expanded={open}
        onClick={() => {
          setOpen(!open);
          setMenu('');
        }}
      >
        <svg width="14" height="14" viewBox="0 0 16 16" aria-hidden="true">
          <path d="M4 2h8v12l-4-3-4 3Z" fill="none" stroke="currentColor" />
        </svg>
        <span>
          {active ? (active.name.length > 24 ? active.name.slice(0, 23) + '…' : active.name) : 'Views'}
          {modified ? ' •' : ''}
        </span>
        <i className="sli-filterChevron" />
      </button>
      {open && (
        <div className="slv-menu" role="menu" aria-label="Saved views">
          {modified && (
            <button
              className="sli-link slv-update"
              disabled={!views.mine.some(v => v.id === active.id) || busy}
              onClick={() => update(active, { state })}
            >
              Update '{active.name}'
            </button>
          )}
          {!all.length ? (
            <p>No saved views yet. Set the filters you want and press Save view.</p>
          ) : (
            <>
              <h3>MY VIEWS</h3>
              {views.mine.map(view => row(view))}
              <h3>SHARED WITH SLT</h3>
              {views.shared.map(view => row(view))}
              {!views.shared.length && <p>No shared views yet.</p>}
            </>
          )}
          <footer>
            <button
              className="sli-link"
              onClick={() => {
                setOpen(false);
                history.push('/slt/reports');
              }}
            >
              Manage in Reports →
            </button>
            <button className="sli-link" onClick={begin}>
              Save current view
            </button>
          </footer>
          {error && <p role="alert">{error}</p>}
        </div>
      )}
    </div>
  );
  const reports = (
    <Card title="Saved views" subtitle="Your views and views shared with SLT">
      {all.map(view => row(view, true))}
      {!all.length && <p className="sli-muted">No saved views yet. Set the filters you want and press Save view.</p>}
      {error && <p role="alert">{error}</p>}
    </Card>
  );
  const feedback = toast ? (
    <div className="slv-toast" role="status">
      Saved to Views{' '}
      <button
        className="sli-link"
        onClick={() => {
          setToast(false);
          setOpen(true);
        }}
      >
        Open Views
      </button>
      <button className="sli-link" aria-label="Dismiss notification" onClick={() => setToast(false)}>
        ×
      </button>
    </div>
  ) : null;
  return { header, toolbar, reports, feedback };
}
